/**
 * Calendar (/schedules) — FEA-21, docs/FRONTEND-PLAN.md §2.4.
 *
 * Two views over one merged timeline:
 *   • month  — a 7-column grid (≥768px only; grids do not degrade below that)
 *   • agenda — the rolling list, and the ONLY view on phones
 * View + anchor month live in the URL (`?view=month&date=2026-08`) so a
 * calendar position is linkable and survives a reload.
 *
 * Two data sources are merged:
 *   1. `GET /schedules` — the caller's schedules (requestor or attendee). The
 *      backend windows on the MASTER record's stored startTime and never
 *      expands a recurrence, so this page fetches with a 366-day look-back
 *      (a weekly series created last year is still a year-old master) and
 *      expands client-side via lib/recurrence.js. Cursors are followed to
 *      exhaustion, bounded at MAX_PAGES with a toast if we stop early.
 *   2. `GET /events` — member events; non-staff callers only ever receive
 *      published ones, so no client-side status filter is needed.
 *
 * Recurring instances have no backend identity: respond/edit/delete all act on
 * the master, i.e. on the whole series. The detail modal says so out loud.
 *
 * Export (FEA-27, §4.1) is entirely client-side: lib/ics.js turns whatever is
 * already in memory into an RFC 5545 document — one occurrence, a whole
 * recurring series (master DTSTART + RRULE), or every entry currently on
 * screen. There is no export endpoint and none is needed.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useConfirm } from '../lib/confirm';
import * as schedulesApi from '../api/schedules';
import * as eventsApi from '../api/events';
import { expandOccurrences, describeRecurrence } from '../lib/recurrence';
import {
  buildVevent,
  buildIcsCalendar,
  scheduleRrule,
  googleCalendarUrl,
  downloadIcs,
  slugify
} from '../lib/ics';
import Modal from '../components/Modal';
import Tabs from '../components/Tabs';
import PersonalEventForm, { scheduleToForm } from '../components/PersonalEventForm';
import { useToast } from '../components/toast';
import * as usersApi from '../api/users';
import {
  ErrorAlert,
  Spinner,
  EmptyState,
  StatusPill,
  UserChip,
  formatDateTime
} from '../components/ui';
import { ui, cn } from '../lib/ui';

/*
 * Calendar classes (FEA-21; replaces components/calendar.css, docs/Decisions.md
 * D41). Colour language: SCHEDULES are brand, MEMBER EVENTS are accent. Both
 * also carry a 3px left rule, so colour is never the only channel. The
 * 7-column grid is gated at 768px; below that the agenda view is forced.
 */
const cal = {
  toolbar: 'mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between',
  toolbarActions: 'flex flex-wrap items-center gap-3',
  legend: 'mt-0 mb-4 flex flex-wrap gap-4 text-body-s text-dcon-ink-muted',
  legendItem: 'inline-flex items-center gap-2',
  swatch: 'inline-block size-3.5 rounded-dcon-sm border',
  card: 'rounded-z-card border border-z-line bg-z-surface p-4 shadow-dcon-md md:p-6',
  nav: 'mb-4 flex flex-wrap items-center gap-3 [&_[role=status]]:m-0',
  navButtons: 'flex gap-2',
  monthTitle: 'm-0 text-title-m md:mr-auto',
  grid: 'overflow-hidden rounded-dcon-md border border-z-line',
  week: 'grid grid-cols-7',
  weekdays: 'border-b border-z-line bg-z-sunken',
  weekday: 'p-2 text-center font-display text-eyebrow uppercase text-dcon-ink-muted',
  day: 'flex min-h-26 cursor-pointer flex-col gap-1 border-z-line p-2 hover:bg-dcon-brand-soft focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-dcon-focus lg:min-h-32',
  daynum: 'self-start font-display text-body-s leading-normal font-semibold',
  chips: 'flex min-w-0 flex-col gap-0.5',
  chip: 'flex w-full cursor-pointer items-baseline gap-1 overflow-hidden rounded-dcon-sm border-l-3 px-2 py-0.5 text-left text-caption text-dcon-ink hover:brightness-96 lg:text-body-s',
  chipTime: 'whitespace-nowrap text-dcon-ink-muted',
  chipTitle: 'truncate',
  more: 'cursor-pointer bg-transparent px-2 text-left text-caption text-dcon-link hover:underline',
  daylist: 'm-0 flex list-none flex-col gap-2 p-0',
  item: 'flex w-full cursor-pointer flex-wrap items-baseline gap-2 rounded-dcon-md border border-z-line border-l-3 bg-z-surface px-3 py-2 text-left hover:bg-z-sunken',
  time: 'whitespace-nowrap text-body-s text-dcon-ink-muted',
  title: 'font-semibold text-dcon-ink',
  agendaTitle: 'mt-0 mb-4 text-title-s',
  agendaGroup: 'mb-6',
  agendaDay: 'mt-0 mb-2 border-b border-z-line pb-1 text-body-s font-semibold text-dcon-ink-muted',
  agendaMeta: 'ml-auto inline-flex flex-wrap gap-1',
  detailWhen: 'mt-0 font-semibold text-dcon-ink',
  detailMessage: 'whitespace-pre-wrap'
};

/** Kind colour for chips and swatches (fill + rule). */
const KIND_FILL = {
  schedule: 'border-dcon-brand bg-dcon-brand-soft',
  event: 'border-dcon-accent bg-dcon-accent-soft'
};

/** Kind colour for the left rule of a list item. */
const KIND_RULE = {
  schedule: 'border-l-dcon-brand',
  event: 'border-l-dcon-accent'
};

const DAY_MS = 24 * 60 * 60 * 1000;
/** Rolling window of the agenda view, unchanged from the pre-FEA-21 page. */
const AGENDA_DAYS = 90;
/** Look-back on the schedules fetch — see the file header. */
const LOOKBACK_DAYS = 366;
const PAGE_LIMIT = 100; // backend hard-caps `limit` at 100
const MAX_PAGES = 10; // 1000 rows is far past any real calendar month
const MAX_CELL_ENTRIES = 3;
const MULTI_DAY_CELL_CAP = 62; // guard: never paint one entry across a whole year

const RESPONSES = ['accepted', 'tentative', 'declined'];
const WEEKDAY_HEADS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const VIEWS = [
  { id: 'month', label: 'Month' },
  { id: 'agenda', label: 'Agenda' }
];

/* ------------------------------------------------------------------ dates */
/* All grid maths is LOCAL time — the user reads local days. Only the
 * recurrence expander works in UTC instants (see lib/recurrence.js). */

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const addDays = (date, n) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);

const pad2 = (n) => String(n).padStart(2, '0');
const dayKey = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const monthParam = (year, month) => `${year}-${pad2(month + 1)}`;

function parseMonthParam(value) {
  const match = /^(\d{4})-(\d{2})$/.exec(value || '');
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  if (month < 0 || month > 11) return null;
  return { year, month };
}

/** Full weeks (5 or 6) covering a month, Sunday-first. */
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const lead = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cellCount = Math.ceil((lead + daysInMonth) / 7) * 7;
  const gridStart = addDays(first, -lead);
  const days = [];
  for (let i = 0; i < cellCount; i += 1) days.push(addDays(gridStart, i));
  return { gridStart, gridEnd: addDays(gridStart, cellCount), days };
}

function timeLabel(date) {
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function longDate(date) {
  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

function intersects(startMs, endMs, fromMs, toMs) {
  if (endMs <= startMs) return startMs >= fromMs && startMs < toMs;
  return startMs < toMs && endMs > fromMs;
}

/* ------------------------------------------------------------------ fetch */

/**
 * Follow `lastEvaluatedKey` to exhaustion, bounded. `onTruncated` fires when
 * the bound is hit with a cursor still outstanding — the page warns instead of
 * silently showing a partial month.
 */
async function fetchAllPages(fetchPage, pick, onTruncated) {
  const all = [];
  let cursor = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    // Sequential by nature: each request needs the previous page's cursor.
    const data = await fetchPage(cursor);
    pick(data).forEach((item) => all.push(item));
    cursor = (data && data.lastEvaluatedKey) || null;
    if (!cursor) return all;
  }
  onTruncated();
  return all;
}

function fetchSchedules(rangeStart, rangeEnd, onTruncated) {
  const startAfter = new Date(rangeStart.getTime() - LOOKBACK_DAYS * DAY_MS).toISOString();
  const startBefore = rangeEnd.toISOString();
  return fetchAllPages(
    (cursor) =>
      // The schedules service is strict about cursors (400 on a malformed
      // one), so only ever hand back what it gave us.
      schedulesApi.listSchedules({
        startAfter,
        startBefore,
        limit: PAGE_LIMIT,
        lastEvaluatedKey: cursor
      }),
    (data) => (data && data.schedules) || [],
    onTruncated
  );
}

function fetchEvents(onTruncated) {
  return fetchAllPages(
    (cursor) => eventsApi.listEvents({ limit: PAGE_LIMIT, lastEvaluatedKey: cursor }),
    (data) => (data && data.events) || [],
    onTruncated
  );
}

/**
 * Both sources for one window. Failures are per-source: a broken /events does
 * not blank the schedules (and vice versa) — whatever loaded still renders
 * next to the error, and `loading` always resolves.
 */
function useCalendarData(rangeStartMs, rangeEndMs) {
  const toast = useToast();
  const [state, setState] = useState({
    loading: true,
    error: null,
    schedules: [],
    events: []
  });

  const runIdRef = useRef(0);
  useEffect(
    () => () => {
      runIdRef.current += 1;
    },
    []
  );

  const load = useCallback(() => {
    const runId = (runIdRef.current += 1);
    setState((current) => ({ ...current, loading: true, error: null }));

    const truncated = [];
    Promise.allSettled([
      fetchSchedules(new Date(rangeStartMs), new Date(rangeEndMs), () =>
        truncated.push('schedules')
      ),
      fetchEvents(() => truncated.push('member events'))
    ]).then(([scheduleResult, eventResult]) => {
      if (runId !== runIdRef.current) return;
      setState({
        loading: false,
        error:
          scheduleResult.status === 'rejected'
            ? scheduleResult.reason
            : eventResult.status === 'rejected'
              ? eventResult.reason
              : null,
        schedules: scheduleResult.status === 'fulfilled' ? scheduleResult.value : [],
        events: eventResult.status === 'fulfilled' ? eventResult.value : []
      });
      truncated.forEach((source) => {
        toast.error(
          `Only the first ${MAX_PAGES * PAGE_LIMIT} ${source} were loaded for this range.`
        );
      });
    });
  }, [rangeStartMs, rangeEndMs, toast]);

  useEffect(load, [load]);

  return useMemo(() => ({ ...state, reload: load }), [state, load]);
}

/* ---------------------------------------------------------------- entries */

/**
 * One display entry — a schedule OCCURRENCE (already expanded) or a member
 * event. `schedule` is always the master; recurring occurrences share it.
 */
function buildEntries(schedules, events, rangeStartMs, rangeEndMs) {
  const rangeStart = new Date(rangeStartMs);
  const rangeEnd = new Date(rangeEndMs);
  const entries = [];

  schedules.forEach((schedule) => {
    expandOccurrences(schedule, rangeStart, rangeEnd).forEach((occurrence) => {
      entries.push({
        kind: 'schedule',
        key: occurrence.key,
        start: occurrence.occurrenceStart,
        end: occurrence.occurrenceEnd,
        title: schedule.subject || '(no subject)',
        location: schedule.location || '',
        schedule,
        occurrence
      });
    });
  });

  events.forEach((event) => {
    const start = new Date(event.startsAt);
    if (Number.isNaN(start.getTime())) return;
    const parsedEnd = event.endsAt ? new Date(event.endsAt) : null;
    const end = parsedEnd && !Number.isNaN(parsedEnd.getTime()) ? parsedEnd : start;
    if (!intersects(start.getTime(), end.getTime(), rangeStartMs, rangeEndMs)) return;
    entries.push({
      kind: 'event',
      key: `event#${event.eventId}`,
      start,
      end,
      title: event.name || '(untitled event)',
      location: event.venue || '',
      event
    });
  });

  entries.sort((a, b) => a.start - b.start || a.title.localeCompare(b.title));
  return entries;
}

/**
 * Bucket entries by local day. A multi-day entry appears on every day it
 * covers inside the window (clipped to it) — `end - 1ms` so something ending
 * at midnight does not bleed into the next cell.
 */
function groupByDay(entries, rangeStartMs, rangeEndMs) {
  const byDay = new Map();
  entries.forEach((entry) => {
    const firstMs = Math.max(entry.start.getTime(), rangeStartMs);
    const lastMs = Math.min(
      Math.max(entry.end.getTime() - 1, entry.start.getTime()),
      rangeEndMs - 1
    );
    let cursor = startOfDay(new Date(firstMs));
    const last = startOfDay(new Date(lastMs));
    for (let i = 0; cursor <= last && i < MULTI_DAY_CELL_CAP; i += 1) {
      const key = dayKey(cursor);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(entry);
      cursor = addDays(cursor, 1);
    }
  });
  return byDay;
}

/* ----------------------------------------------------------------- export */

/**
 * UID suffix. UIDs must be globally unique and STABLE — re-importing the same
 * occurrence should update the user's existing entry, not duplicate it — so
 * they are derived from the backend id (plus the occurrence instant for a
 * single instance of a series), never from a random value or the clock.
 */
const UID_DOMAIN = 'project-cap';

/** Same id fallback recurrence.js uses when it builds occurrence keys. */
const scheduleId = (schedule) => schedule.id || schedule.scheduleId || 'schedule';

/** VEVENT lines for ONE expanded occurrence (or a one-off schedule). */
function occurrenceVevent(entry, stampDate) {
  const { schedule, occurrence } = entry;
  const recurring = Boolean(occurrence && occurrence.isRecurring);
  const description = `${schedule.message || ''}${
    recurring ? ' (part of a recurring series)' : ''
  }`.trim();
  return buildVevent({
    uid: `${scheduleId(schedule)}#${entry.start.toISOString()}@${UID_DOMAIN}`,
    title: schedule.subject || '',
    description,
    location: schedule.location || '',
    start: entry.start,
    end: entry.end,
    stampDate
  });
}

/**
 * VEVENT lines for a whole recurring series: the MASTER's own times plus an
 * RRULE, so the user's calendar expands it natively (and keeps expanding it
 * past whatever window this page happens to be showing). When the rule cannot
 * be expressed as an RRULE, `scheduleRrule` returns null and this degrades to
 * exactly one event at the master's time — the same thing recurrence.js does
 * with an unrecognised frequency.
 */
function seriesVevent(schedule, stampDate) {
  return buildVevent({
    uid: `${scheduleId(schedule)}@${UID_DOMAIN}`,
    title: schedule.subject || '',
    description: schedule.message || '',
    location: schedule.location || '',
    start: schedule.startTime,
    end: schedule.endTime,
    rrule: scheduleRrule(schedule.recurrence) || undefined,
    stampDate
  });
}

/** VEVENT lines for a member event. `endsAt` is optional; DTEND is then omitted. */
function eventVevent(entry, stampDate) {
  const event = entry.event;
  return buildVevent({
    uid: `${event.eventId}@${UID_DOMAIN}`,
    title: event.name || '',
    description: event.description || '',
    location: event.venue || '',
    start: entry.start,
    end: entry.end,
    stampDate
  });
}

const entryVevent = (entry, stampDate) =>
  entry.kind === 'event' ? eventVevent(entry, stampDate) : occurrenceVevent(entry, stampDate);

/** Google Calendar template link for a single entry (never a series — see ics.js). */
function entryGoogleUrl(entry) {
  const shared = { start: entry.start, end: entry.end, location: entry.location || '' };
  if (entry.kind === 'event') {
    return googleCalendarUrl({
      ...shared,
      title: entry.event.name || '',
      description: entry.event.description || ''
    });
  }
  return googleCalendarUrl({
    ...shared,
    title: entry.schedule.subject || '',
    description: entry.schedule.message || ''
  });
}

/**
 * The one place an .ics leaves the app: build → download → toast. DTSTAMP is
 * stamped HERE rather than inside lib/ics.js, which is how that module stays
 * pure and unit-testable (it takes `stampDate` as a parameter).
 */
function useIcsExport() {
  const toast = useToast();
  return useCallback(
    (filename, vevents) => {
      const usable = vevents.filter((lines) => lines.length > 0);
      if (usable.length === 0) {
        toast.error('Nothing to export.');
        return;
      }
      if (downloadIcs(filename, buildIcsCalendar(usable))) {
        // Mobile browsers hand a text/calendar download straight to the
        // calendar app's import sheet, so "downloaded" is the honest word on
        // desktop and the right hint on a phone.
        toast.success(`Downloaded ${filename}`);
      } else {
        toast.error('This browser cannot download files.');
      }
    },
    [toast]
  );
}

/** Shared "Google Calendar" affordance — single entries only. */
function GoogleCalendarLink({ entry }) {
  const href = entryGoogleUrl(entry);
  if (!href) return null;
  return (
    <a className={ui.btnSecondary} href={href} target="_blank" rel="noopener noreferrer">
      Google Calendar
    </a>
  );
}

/* ------------------------------------------------------------------- page */

export default function Schedules() {
  const { user } = useAuth();
  const userId = user && user.userId;
  const toast = useToast();
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();

  // The month grid needs real width; below 768px agenda is the only view, and
  // that is enforced here rather than only in CSS so a `?view=month` link on a
  // phone still renders something usable.
  const isWide = useMediaQuery('(min-width: 768px)');

  const [initialMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [today] = useState(() => startOfDay(new Date()));

  const requestedView = params.get('view');
  const storedView = requestedView === 'month' || requestedView === 'agenda' ? requestedView : null;
  const view = isWide ? storedView || 'month' : 'agenda';

  const parsedMonth = parseMonthParam(params.get('date'));
  const year = parsedMonth ? parsedMonth.year : initialMonth.year;
  const month = parsedMonth ? parsedMonth.month : initialMonth.month;

  const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);

  const rangeStartMs = view === 'month' ? grid.gridStart.getTime() : today.getTime();
  const rangeEndMs =
    view === 'month' ? grid.gridEnd.getTime() : today.getTime() + AGENDA_DAYS * DAY_MS;

  const data = useCalendarData(rangeStartMs, rangeEndMs);
  const { reload } = data;

  const entries = useMemo(
    () => buildEntries(data.schedules, data.events, rangeStartMs, rangeEndMs),
    [data.schedules, data.events, rangeStartMs, rangeEndMs]
  );
  const entriesByDay = useMemo(
    () => groupByDay(entries, rangeStartMs, rangeEndMs),
    [entries, rangeStartMs, rangeEndMs]
  );

  const exportIcs = useIcsExport();

  /**
   * Export exactly what is on screen — the already-expanded entries, schedule
   * occurrences and member events alike, as individual VEVENTs with no RRULEs.
   * In month view that is the whole 5/6-week grid, so the leading/trailing days
   * of the neighbouring months come along: the grid shows them, so the export
   * carries them. "Export series" on a detail modal is the RRULE-carrying door.
   */
  const exportVisible = () => {
    const stampDate = new Date();
    const filename =
      view === 'month'
        ? `calendar-${monthParam(year, month)}.ics`
        : `calendar-agenda-${dayKey(today)}.ics`;
    exportIcs(
      filename,
      entries.map((entry) => entryVevent(entry, stampDate))
    );
  };

  const [dayModal, setDayModal] = useState(null); // Date
  const [selected, setSelected] = useState(null); // entry
  const [editing, setEditing] = useState(null); // schedule master with resolved users
  const [creating, setCreating] = useState(false);

  const selectParam = params.get('select');
  const didAutoSelect = useRef(false);
  useEffect(() => {
    if (!selectParam || data.loading || didAutoSelect.current) return;
    const match = entries.find(
      (e) => e.kind === 'schedule' && e.schedule && e.schedule.id === selectParam
    );
    if (match) {
      setSelected(match);
      didAutoSelect.current = true;
    }
  }, [selectParam, data.loading, entries]);

  const openEdit = useCallback(async (schedule) => {
    const attendeeIds = schedule.attendees || [];
    const sw = schedule.sharedWith || { users: [], groups: [] };
    const sharedUserIds = sw.users || [];

    const resolve = async (ids) => {
      const results = await Promise.allSettled(ids.map((id) => usersApi.getUser(id)));
      return results.map((r, i) =>
        r.status === 'fulfilled' && r.value && r.value.user
          ? { userId: r.value.user.userId, displayName: [r.value.user.firstName, r.value.user.lastName].filter(Boolean).join(' ') || null, email: r.value.user.email }
          : { userId: ids[i], displayName: null, email: null }
      );
    };

    const [resolvedAttendees, resolvedShared] = await Promise.all([
      attendeeIds.length ? resolve(attendeeIds) : [],
      sharedUserIds.length ? resolve(sharedUserIds) : []
    ]);

    setEditing({ ...schedule, _resolvedAttendees: resolvedAttendees, _resolvedShared: resolvedShared });
  }, []);

  const setView = (next) => {
    setParams(
      (current) => {
        const nextParams = new URLSearchParams(current);
        nextParams.set('view', next);
        if (next === 'month') nextParams.set('date', monthParam(year, month));
        else nextParams.delete('date');
        return nextParams;
      },
      { replace: true }
    );
  };

  const goToMonth = (nextYear, nextMonth) => {
    setParams(
      (current) => {
        const nextParams = new URLSearchParams(current);
        nextParams.set('view', 'month');
        nextParams.set('date', monthParam(nextYear, nextMonth));
        return nextParams;
      },
      { replace: true }
    );
  };

  const shiftMonth = (delta) => {
    const anchor = new Date(year, month + delta, 1);
    goToMonth(anchor.getFullYear(), anchor.getMonth());
  };

  async function respond(schedule, response) {
    try {
      await schedulesApi.respondToSchedule(schedule.id, response);
      toast.success(`Response saved: ${response}.`);
      setSelected(null);
      reload();
    } catch (err) {
      toast.error(err);
    }
  }

  async function remove(schedule) {
    const recurring = Boolean(schedule.recurrence);
    const ok = await confirm({
      title: recurring ? 'Cancel the whole series?' : 'Cancel this event?',
      message: recurring
        ? `"${schedule.subject}" repeats — cancelling removes every occurrence and notifies attendees.`
        : `"${schedule.subject}" will be removed and attendees notified.`,
      confirmLabel: 'Cancel event',
      cancelLabel: 'Keep it',
      danger: true
    });
    if (!ok) return;
    try {
      await schedulesApi.deleteSchedule(schedule.id);
      toast.success('Event cancelled.');
      setSelected(null);
      reload();
    } catch (err) {
      toast.error(err);
    }
  }

  async function create(payload) {
    await schedulesApi.createSchedule(payload);
    toast.success('Event created.');
    setCreating(false);
    reload();
  }

  async function save(payload) {
    await schedulesApi.updateSchedule(editing.id, payload);
    toast.success('Event updated.');
    setEditing(null);
    setSelected(null);
    reload();
  }

  const monthTitle = new Date(year, month, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric'
  });

  return (
    <>
      <div className={cal.toolbar}>
        <h1 className="m-0 text-title-l">Calendar</h1>
        <div className={cal.toolbarActions}>
          {isWide && (
            <Tabs tabs={VIEWS} value={view} onChange={setView} aria-label="Calendar view" />
          )}
          <button
            type="button"
            className={ui.btnSecondary}
            onClick={exportVisible}
            disabled={entries.length === 0}
            title="Download every entry currently shown as an iCalendar file"
          >
            {view === 'month' ? 'Export month (.ics)' : 'Export list (.ics)'}
          </button>
          {/* Agenda keeps the inline create card it always had; the month grid
              has no room for one, so there the same form opens in a modal. */}
          {view === 'month' && (
            <button type="button" className={ui.btnPrimary} onClick={() => setCreating(true)}>
              New event
            </button>
          )}
        </div>
      </div>

      <CalendarLegend />
      <ErrorAlert error={data.error} />
      {data.error && (
        <p>
          <button type="button" className={ui.btnSecondary} onClick={reload}>
            Retry
          </button>
        </p>
      )}

      {view === 'month' ? (
        <section className={cal.card}>
          <div className={cal.nav}>
            <div className={cal.navButtons}>
              <button
                type="button"
                className={ui.btnSecondary}
                onClick={() => shiftMonth(-1)}
                aria-label="Previous month"
              >
                ‹
              </button>
              <button
                type="button"
                className={ui.btnSecondary}
                onClick={() => goToMonth(today.getFullYear(), today.getMonth())}
              >
                Today
              </button>
              <button
                type="button"
                className={ui.btnSecondary}
                onClick={() => shiftMonth(1)}
                aria-label="Next month"
              >
                ›
              </button>
            </div>
            <h2 className={cal.monthTitle}>{monthTitle}</h2>
            {data.loading && <Spinner label="Loading month…" />}
          </div>
          <MonthGrid
            year={year}
            month={month}
            grid={grid}
            today={today}
            entriesByDay={entriesByDay}
            onOpenDay={setDayModal}
            onSelectEntry={setSelected}
          />
        </section>
      ) : (
        <div className={cn(ui.grid, ui.gridMd2)}>
          <AgendaView
            entriesByDay={entriesByDay}
            loading={data.loading}
            userId={userId}
            onSelectEntry={setSelected}
          />
          <section className={cal.card}>
            <h2 className={cal.agendaTitle}>New event</h2>
            <PersonalEventForm onSubmit={create} />
          </section>
        </div>
      )}

      <Modal
        open={Boolean(dayModal)}
        onClose={() => setDayModal(null)}
        title={dayModal ? longDate(dayModal) : ''}
      >
        {dayModal && (
          <DayEntryList
            entries={entriesByDay.get(dayKey(dayModal)) || []}
            onSelectEntry={(entry) => {
              setDayModal(null);
              setSelected(entry);
            }}
          />
        )}
      </Modal>

      <Modal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected ? selected.title : ''}
      >
        {selected && selected.kind === 'schedule' && (
          <ScheduleDetail
            entry={selected}
            userId={userId}
            onRespond={respond}
            onEdit={openEdit}
            onDelete={remove}
          />
        )}
        {selected && selected.kind === 'event' && <EventDetail entry={selected} />}
      </Modal>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing ? `Edit “${editing.subject}”` : ''}
      >
        {editing && (
          <>
            {editing.recurrence && (
              <p className={cn(ui.muted, 'text-body-s')}>
                This is a repeating event — changes apply to the whole series.
              </p>
            )}
            <PersonalEventForm
              key={editing.id}
              initial={scheduleToForm(editing, editing._resolvedAttendees, editing._resolvedShared)}
              submitLabel="Save changes"
              warnOnTimeChange
              onSubmit={save}
              onCancel={() => setEditing(null)}
            />
          </>
        )}
      </Modal>

      <Modal open={creating} onClose={() => setCreating(false)} title="New event">
        {creating && (
          <PersonalEventForm
            onSubmit={create}
            onCancel={() => setCreating(false)}
          />
        )}
      </Modal>
    </>
  );
}

/** `window.matchMedia` as reactive state, with a listener so rotation counts. */
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const list = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

function CalendarLegend() {
  return (
    <p className={cal.legend}>
      <span className={cal.legendItem}>
        <span className={cn(cal.swatch, KIND_FILL.schedule)} aria-hidden="true" /> Your schedule
      </span>
      <span className={cal.legendItem}>
        <span className={cn(cal.swatch, KIND_FILL.event)} aria-hidden="true" /> Member events
      </span>
    </p>
  );
}

/* ------------------------------------------------------------- month grid */

/**
 * 7-column month grid with a roving tabindex: exactly one cell is tabbable,
 * arrows move the focused day (clamped to the grid — arrowing off the edge
 * does not silently change month), Home/End jump within the week, Enter/Space
 * opens the day. Entry chips are `tabIndex={-1}` so Tab walks days, not
 * dozens of chips; everything in a cell is reachable through the day modal.
 */
function MonthGrid({ year, month, grid, today, entriesByDay, onOpenDay, onSelectEntry }) {
  const todayKey = dayKey(today);
  const defaultKey = useMemo(() => {
    const inGrid = grid.days.some((day) => dayKey(day) === todayKey);
    return inGrid ? todayKey : dayKey(new Date(year, month, 1));
  }, [grid, todayKey, year, month]);

  const [focusKey, setFocusKey] = useState(defaultKey);
  const cellRefs = useRef({});
  const pendingFocus = useRef(false);

  useEffect(() => {
    setFocusKey(defaultKey);
  }, [defaultKey]);

  useEffect(() => {
    if (!pendingFocus.current) return;
    pendingFocus.current = false;
    const node = cellRefs.current[focusKey];
    if (node) node.focus();
  }, [focusKey]);

  const move = (fromIndex, delta) => {
    const next = Math.min(Math.max(fromIndex + delta, 0), grid.days.length - 1);
    pendingFocus.current = true;
    setFocusKey(dayKey(grid.days[next]));
  };

  const handleKeyDown = (event, index, day) => {
    const deltas = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (event.key in deltas) {
      event.preventDefault();
      move(index, deltas[event.key]);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      move(index, -(index % 7));
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      move(index, 6 - (index % 7));
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenDay(day);
    }
  };

  const weeks = [];
  for (let i = 0; i < grid.days.length; i += 7) weeks.push(grid.days.slice(i, i + 7));

  return (
    <div
      className={cal.grid}
      role="grid"
      aria-label={new Date(year, month, 1).toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric'
      })}
    >
      <div className={cn(cal.week, cal.weekdays)} role="row">
        {WEEKDAY_HEADS.map((label) => (
          <div key={label} role="columnheader" className={cal.weekday}>
            {label}
          </div>
        ))}
      </div>
      {weeks.map((week, weekIndex) => (
        <div className={cal.week} role="row" key={dayKey(week[0])}>
          {week.map((day, dayIndex) => {
            const index = weekIndex * 7 + dayIndex;
            const key = dayKey(day);
            const dayEntries = entriesByDay.get(key) || [];
            const outside = day.getMonth() !== month;
            const isToday = key === todayKey;
            const overflow = dayEntries.length - MAX_CELL_ENTRIES;
            return (
              <div
                key={key}
                role="gridcell"
                className={cn(
                  cal.day,
                  dayIndex < 6 && 'border-r',
                  weekIndex < weeks.length - 1 && 'border-b',
                  isToday ? 'bg-dcon-brand-soft' : outside ? 'bg-z-sunken' : 'bg-z-surface'
                )}
                tabIndex={key === focusKey ? 0 : -1}
                aria-label={`${longDate(day)}, ${dayEntries.length} ${
                  dayEntries.length === 1 ? 'entry' : 'entries'
                }`}
                ref={(node) => {
                  cellRefs.current[key] = node;
                }}
                onFocus={() => setFocusKey(key)}
                onKeyDown={(event) => handleKeyDown(event, index, day)}
                onClick={() => onOpenDay(day)}
              >
                <span
                  className={cn(
                    cal.daynum,
                    isToday
                      ? 'rounded-dcon-pill bg-dcon-brand px-2 text-dcon-on-brand'
                      : outside
                        ? 'text-dcon-ink-subtle'
                        : 'text-dcon-ink-body'
                  )}
                  aria-hidden="true"
                >
                  {day.getDate()}
                </span>
                <div className={cal.chips}>
                  {dayEntries.slice(0, MAX_CELL_ENTRIES).map((entry) => (
                    <button
                      key={entry.key}
                      type="button"
                      tabIndex={-1}
                      className={cn(cal.chip, KIND_FILL[entry.kind])}
                      title={entry.title}
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelectEntry(entry);
                      }}
                    >
                      <span className={cal.chipTime}>{timeLabel(entry.start)}</span>
                      <span className={cal.chipTitle}>{entry.title}</span>
                    </button>
                  ))}
                  {overflow > 0 && (
                    <button
                      type="button"
                      tabIndex={-1}
                      className={cal.more}
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenDay(day);
                      }}
                    >
                      +{overflow} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function DayEntryList({ entries, onSelectEntry }) {
  if (entries.length === 0) return <EmptyState>Nothing scheduled.</EmptyState>;
  return (
    <ul className={cal.daylist}>
      {entries.map((entry) => (
        <li key={entry.key}>
          <button
            type="button"
            className={cn(cal.item, KIND_RULE[entry.kind])}
            onClick={() => onSelectEntry(entry)}
          >
            <span className={cal.time}>
              {timeLabel(entry.start)} – {timeLabel(entry.end)}
            </span>
            <span className={cal.title}>{entry.title}</span>
            {entry.location && <span className={cn(ui.muted, 'text-body-s')}>{entry.location}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

/* ----------------------------------------------------------------- agenda */

/**
 * Agenda reads the SAME day buckets the grid does, so a multi-day entry and a
 * range-clipped one behave identically in both views. `dayKey` is
 * `YYYY-MM-DD`, so a lexicographic sort is a chronological one.
 */
function AgendaView({ entriesByDay, loading, userId, onSelectEntry }) {
  const groups = useMemo(
    () =>
      [...entriesByDay.entries()]
        .sort((a, b) => (a[0] < b[0] ? -1 : 1))
        .map(([key, items]) => {
          const [y, m, d] = key.split('-').map(Number);
          return { key, date: new Date(y, m - 1, d), items };
        }),
    [entriesByDay]
  );

  return (
    <section className={cal.card}>
      <h2 className={cal.agendaTitle}>Next {AGENDA_DAYS} days</h2>
      {loading ? (
        <Spinner />
      ) : groups.length === 0 ? (
        <EmptyState>Nothing on the calendar in the next {AGENDA_DAYS} days.</EmptyState>
      ) : (
        groups.map((group) => (
          <div className={cal.agendaGroup} key={group.key}>
            <h3 className={cal.agendaDay}>{longDate(group.date)}</h3>
            {group.items.map((entry) => (
              <button
                key={entry.key}
                type="button"
                className={cn(cal.item, KIND_RULE[entry.kind], 'mb-2')}
                onClick={() => onSelectEntry(entry)}
              >
                <span className={cal.time}>
                  {timeLabel(entry.start)} – {timeLabel(entry.end)}
                </span>
                <span className={cal.title}>{entry.title}</span>
                <span className={cal.agendaMeta}>
                  {entry.kind === 'event' ? (
                    <span className={ui.pillInfo}>member event</span>
                  ) : entry.schedule.requestor === userId ? (
                    <span className={ui.pillNeutral}>organizer</span>
                  ) : (
                    <StatusPill status={entry.schedule.response || 'pending'} />
                  )}
                  {entry.kind === 'schedule' && entry.schedule.recurrence && (
                    <span className={ui.pillNeutral}>repeats</span>
                  )}
                  {entry.kind === 'schedule' && entry.schedule.showTimeAs && entry.schedule.showTimeAs !== 'busy' && (
                    <span className={ui.pillNeutral}>{entry.schedule.showTimeAs}</span>
                  )}
                  {entry.kind === 'schedule' && entry.schedule.visibility === 'shared' && (
                    <span className={ui.pillInfo}>shared</span>
                  )}
                  {entry.kind === 'schedule' && (entry.schedule.attendees || []).length > 0 && (
                    <span className={ui.pillNeutral}>
                      {(entry.schedule.attendees || []).length} attendee{(entry.schedule.attendees || []).length === 1 ? '' : 's'}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        ))
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ detail */

function ScheduleDetail({ entry, userId, onRespond, onEdit, onDelete }) {
  const { schedule, occurrence } = entry;
  const isRequestor = schedule.requestor === userId;
  const attendees = schedule.attendees || [];
  const sw = schedule.sharedWith || { users: [], groups: [] };
  const isAttendee = attendees.includes(userId);
  const [busy, setBusy] = useState(false);
  const exportIcs = useIcsExport();
  const recurring = Boolean(occurrence && occurrence.isRecurring);

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };

  const stem = slugify(schedule.subject);
  const exportOccurrence = () =>
    exportIcs(`${stem}.ics`, [occurrenceVevent(entry, new Date())]);
  const exportSeries = () =>
    exportIcs(`${stem}-series.ics`, [seriesVevent(schedule, new Date())]);

  return (
    <>
      <p className={cal.detailWhen}>
        {formatDateTime(entry.start.toISOString())} – {timeLabel(entry.end)}
        {schedule.timezone ? <span className={cn(ui.muted, 'text-body-s')}> ({schedule.timezone})</span> : null}
      </p>
      {recurring && (
        <div className={ui.alertInfo} role="note">
          One occurrence of a repeating event ({describeRecurrence(schedule.recurrence)}).
          Responding, editing and cancelling apply to the whole series — the API has no
          per-occurrence operations.
        </div>
      )}
      {entry.location && <p className="text-body-s">📍 {entry.location}</p>}
      {schedule.message && <p className={cal.detailMessage}>{schedule.message}</p>}
      <p className="text-body-s">
        {isRequestor ? (
          <span className={ui.pillNeutral}>you organize this</span>
        ) : (
          <>
            Your response: <StatusPill status={schedule.response || 'pending'} />
          </>
        )}
        {schedule.showTimeAs && schedule.showTimeAs !== 'busy' && (
          <span className={ui.pillNeutral}>{schedule.showTimeAs}</span>
        )}
        {schedule.visibility === 'shared' && (
          <span className={ui.pillInfo}>shared</span>
        )}
      </p>
      <p className="text-body-s">
        {attendees.length === 0 ? (
          <span className={ui.muted}>No other attendees.</span>
        ) : (
          <>
            <span className={ui.muted}>
              {attendees.length} attendee{attendees.length === 1 ? '' : 's'}:{' '}
            </span>
            {attendees.map((attendeeId) => (
              <UserChip key={attendeeId} userId={attendeeId} />
            ))}
          </>
        )}
      </p>
      {sw.users.length > 0 && (
        <p className="text-body-s">
          <span className={ui.muted}>
            Shared with {sw.users.length} viewer{sw.users.length === 1 ? '' : 's'}:{' '}
          </span>
          {sw.users.map((uid) => (
            <UserChip key={uid} userId={uid} />
          ))}
        </p>
      )}
      {sw.groups.length > 0 && (
        <p className={cn('text-body-s', ui.muted)}>
          Shared with {sw.groups.length} group{sw.groups.length === 1 ? '' : 's'}
        </p>
      )}

      <div className={ui.row}>
        {isAttendee &&
          RESPONSES.map((response) => (
            <button
              key={response}
              type="button"
              className={schedule.response === response ? ui.btnPrimary : ui.btnSecondary}
              disabled={busy || schedule.response === response}
              onClick={() => run(() => onRespond(schedule, response))}
            >
              {response}
            </button>
          ))}
        {isRequestor && (
          <>
            <button type="button" className={ui.btnSecondary} onClick={() => onEdit(schedule)}>
              Edit
            </button>
            <button
              type="button"
              className={ui.btnDanger}
              disabled={busy}
              onClick={() => run(() => onDelete(schedule))}
            >
              Cancel event
            </button>
          </>
        )}
      </div>

      <div className={ui.row}>
        <Link className={ui.btnSecondary} to="/events">
          View in events list
        </Link>
        <button type="button" className={ui.btnSecondary} onClick={exportOccurrence}>
          Add to calendar (.ics)
        </button>
        {recurring && (
          <button type="button" className={ui.btnSecondary} onClick={exportSeries}>
            Export series (.ics)
          </button>
        )}
        <GoogleCalendarLink entry={entry} />
      </div>
      {recurring && (
        <p className={cn(ui.muted, 'text-body-s')}>
          &ldquo;Add to calendar&rdquo; exports this occurrence only; &ldquo;Export
          series&rdquo; exports the repeat rule. Times are exported in UTC.
        </p>
      )}
    </>
  );
}

function EventDetail({ entry }) {
  const event = entry.event;
  const exportIcs = useIcsExport();
  const exportEvent = () =>
    exportIcs(`${slugify(event.name)}.ics`, [eventVevent(entry, new Date())]);
  return (
    <>
      <p className={cal.detailWhen}>
        {formatDateTime(entry.start.toISOString())}
        {event.endsAt ? ` – ${timeLabel(entry.end)}` : ''}
      </p>
      {event.venue && <p className="text-body-s">📍 {event.venue}</p>}
      <p className="text-body-s">
        <StatusPill status={event.status || 'published'} />
        {event.accreditedOnly && <span className={ui.pillWarning}>accredited only</span>}
        {event.capacity ? <span className={ui.pillNeutral}>capacity {event.capacity}</span> : null}
      </p>
      {event.description && <p className={cal.detailMessage}>{event.description}</p>}
      <div className={ui.row}>
        <Link className={ui.btnSecondary} to={`/events/${encodeURIComponent(event.eventId)}`}>
          View event details
        </Link>
        <button type="button" className={ui.btnSecondary} onClick={exportEvent}>
          Add to calendar (.ics)
        </button>
        <GoogleCalendarLink entry={entry} />
      </div>
    </>
  );
}
